import React, { FC, memo } from 'react';
import { ToolType } from '../tools.types';
import cls from './ToolPreviewItem.module.scss';
import { Button, Dropdown } from 'react-bootstrap';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { useEnableOrDisableToolSwitcherMutation } from '../tools.service';
import { isFetchBaseQueryError } from '../../../utils/helpers';
import { useNavigate } from 'react-router-dom';
import routes from '../../../routes';
import { useAppDispatch } from '../../../hooks/defaultHooks';
import { openModal } from '../../../store/slices/modalSlice';

type ToolPreviewItemProps = {
  tool: ToolType;
  refreshHandler: () => void;
};

const ToolPreviewItem: FC<ToolPreviewItemProps> = memo((props) => {
  const { tool, refreshHandler } = props;
  const [changeToolView] = useEnableOrDisableToolSwitcherMutation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const openUpdatePageHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    navigate(routes.pages.updateToolPageById(tool.id));
  };
  /** The whole row opens the tool; the controls inside it stop the click themselves */
  const openToolPageHandler = () => {
    navigate(routes.pages.toolPageById(tool.id));
  };
  const enableToolHandler = async () => {
    try {
      await changeToolView({
        toolId: tool.id,
        switcher: 'enable',
      }).unwrap();
      toast.success('Enabled');
      refreshHandler();
    } catch (error) {
      if (isFetchBaseQueryError(error)) {
        if (error.status === 403) {
          toast.error('Access denied');
        }
      }
    }
  };

  const disableToolHandler = async () => {
    try {
      await changeToolView({ toolId: tool.id, switcher: 'disable' }).unwrap();
      toast.success('Disabled');
      refreshHandler();
    } catch (error) {
      if (isFetchBaseQueryError(error)) {
        if (error.status === 403) {
          toast.error('Access denied');
        }
      }
    }
  };
  const openDisableToolModalHandler = () => {
    dispatch(
      openModal({
        type: 'actionWithTool',
        extra: {
          id: tool?.id,
          title: 'Disable tool?',
          supportText: 'The tool',
          textInformation: tool?.name,
          subSupportText: 'will be removed from the site and unavailable to users',
          handler: disableToolHandler,
          actionButtonColor: 'danger',
          actionButtonText: 'Disable',
        },
      }),
    );
  };

  const openEnableToolModalHandler = () =>
    dispatch(
      openModal({
        type: 'actionWithTool',
        extra: {
          id: tool?.id,
          title: 'Enable tool?',
          supportText: 'The tool',
          textInformation: tool?.name,
          subSupportText: 'will appear on the site and become available to users',
          handler: enableToolHandler,
          actionButtonColor: 'primary',
          actionButtonText: 'Enable',
        },
      }),
    );
  return (
    <div className={cls.ItemPreview} onClick={openToolPageHandler}>
      <div>
        {tool.image && (
          <div className={cls.ImgWrapper}>
            <img src={tool.image} alt={tool.name} className={cls.Img} />
          </div>
        )}
        <span>{tool.name}</span>
      </div>
      <span>{tool.description}</span>
      <span className={tool.enabled ? cls.Status : `${cls.Status} ${cls.StatusDisabled}`}>
        {tool.enabled ? 'Enabled' : 'Disabled'}
      </span>
      <div className={cls.ActionsBlock}>
        <Button className={cls.EditButton} onClick={openUpdatePageHandler}>
          Edit
        </Button>
        <Dropdown id={tool.id} autoClose={true} onClick={(event) => event.stopPropagation()}>
          <Dropdown.Toggle id="dropdown-basic" className={cls.Toggle}>
            <BsThreeDotsVertical />
          </Dropdown.Toggle>
          <Dropdown.Menu className={cls.DropdownMenu}>
            {tool.enabled && (
              <Dropdown.Item
                className={`${cls.StatusDisabled} banner-chip`}
                onClick={openDisableToolModalHandler}
              >
                Disable
              </Dropdown.Item>
            )}
            {!tool.enabled && (
              <Dropdown.Item
                className={`${cls.StatusActive} banner-chip`}
                onClick={openEnableToolModalHandler}
              >
                Enable
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
});

export default ToolPreviewItem;
